/**
 * SizilPro — usePdfGeneration
 * Triggers server-side PDF generation through the Supabase Edge Function proxy.
 *
 * File: src/modules/proposal-builder/hooks/usePdfGeneration.ts
 */

import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

interface GeneratePdfOptions {
  proposalId: string;
  clientId: string;
  onSuccess?: (pdfUrl: string) => void;
  onError?: (error: Error) => void;
}

interface GeneratePdfResult {
  pdfUrl: string;
  pageCount?: number | null;
}

type GeneratePdfResponse = {
  success?: boolean;
  pdfUrl?: string;
  pdf_url?: string;
  pageCount?: number | null;
  page_count?: number | null;
  error?: string;
  message?: string;
};

function toErrorMessage(
  error: unknown,
  fallback = "PDF generation failed"
): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  if (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof (error as { message?: unknown }).message === "string"
  ) {
    return (error as { message: string }).message;
  }

  return fallback;
}

async function ensureFreshSession() {
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();

  if (sessionError) {
    throw new Error(sessionError.message);
  }

  if (!session?.user) {
    throw new Error("Not authenticated");
  }

  const expiresAt = session.expires_at ? session.expires_at * 1000 : 0;
  const now = Date.now();
  const expiringSoon = !expiresAt || expiresAt - now < 60_000;

  if (expiringSoon) {
    const { data, error } = await supabase.auth.refreshSession();

    if (error) {
      throw new Error(error.message);
    }

    if (!data.session?.user) {
      throw new Error("Not authenticated");
    }

    return data.session;
  }

  return session;
}

export function usePdfGeneration() {
  const [progress, setProgress] = useState<
    "idle" | "generating" | "done" | "error"
  >("idle");
  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({
      proposalId,
      clientId: _clientId,
    }: GeneratePdfOptions): Promise<GeneratePdfResult> => {
      setProgress("generating");

      await ensureFreshSession();

      const { data, error } = await supabase.functions.invoke<GeneratePdfResponse>(
        "generate-pdf-proxy",
        {
          body: { proposalId },
        }
      );

      if (error) {
        // Supabase wraps non-2xx edge-function responses with a generic message.
        // Try to extract the real error from the response body first.
        let message = error.message || "PDF generation request failed";
        try {
          const ctx = (error as unknown as { context?: Response }).context;
          if (ctx) {
            const body = await ctx.clone().json() as { error?: string; message?: string };
            if (body?.error)   message = body.error;
            else if (body?.message) message = body.message;
          }
        } catch {
          // ignore — use the generic message
        }
        throw new Error(message);
      }

      if (!data) {
        throw new Error("No response from PDF generator");
      }

      const pdfUrl = data.pdfUrl ?? data.pdf_url;
      if (!pdfUrl) {
        throw new Error(data.error ?? data.message ?? "PDF generation failed");
      }

      return {
        pdfUrl,
        pageCount: data.pageCount ?? data.page_count ?? null,
      };
    },
    onSuccess: (result, variables) => {
      setProgress("done");
      qc.invalidateQueries({
        queryKey: ["proposals", "detail", variables.proposalId],
      });
      qc.invalidateQueries({
        queryKey: ["proposals", variables.clientId, "list"],
      });
      qc.invalidateQueries({
        queryKey: ["proposals", variables.clientId, "pipeline-stats"],
      });
      qc.invalidateQueries({
        queryKey: ["proposals"],
      });
      variables.onSuccess?.(result.pdfUrl);
    },
    onError: (error, variables) => {
      setProgress("error");
      variables.onError?.(new Error(toErrorMessage(error)));
    },
  });

  return {
    generate: (options: GeneratePdfOptions) => mutation.mutate(options),
    generateAsync: (options: GeneratePdfOptions) =>
      mutation.mutateAsync(options),
    progress,
    isGenerating: mutation.isPending,
    pdfUrl: mutation.data?.pdfUrl ?? null,
    pageCount: mutation.data?.pageCount ?? null,
    error: mutation.error ? new Error(toErrorMessage(mutation.error)) : null,
    reset: () => {
      mutation.reset();
      setProgress("idle");
    },
  };
}

export const useViewPdf = usePdfGeneration;
export const useGeneratePdf = usePdfGeneration;